/**
 * @module app.cm
 */


import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { CaseService } from 'app/cm/services/case-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { SaveReferralSourceCustomElement } from 'app/cm/admin/referral/save-referral-source';
import { deepClone } from 'app/shared/lib/utilities';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class ReferredBy extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected referralSources: ReadonlyArray<CM.ReferralSource> = [];

    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 25;
    protected includeInactive = false;
    protected isValid: boolean = false;
    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $modal!: EssModalCustomElement;
    protected $form!: IForm;
    protected $saveReferralSource!: SaveReferralSourceCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly toaster: Toaster,
        private readonly caseService: CaseService,
        ...rest: any[]
    ) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'description',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    private editReferralSource(referralSource: CM.ReferralSource): void {
        if(!referralSource || !this.$modal) {
            return;
        }

        this.$saveReferralSource.referralSource = deepClone(referralSource);
        this.$modal.open();
    }

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadSourcesForReferral();
    }

    protected addReferralSource() {
        this.$modal.open();
    }

    protected modalClose(): void {
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.$saveReferralSource.reset();
        this.$modal.close();
    }

    protected async handleSaveReferralSourceFormSuccess(): Promise<void> {
        if(this.$saveReferralSource.reset) {
            this.$saveReferralSource.reset();
        }
        this.$modal.close();
    }

    public async submit(): Promise<boolean> {
        const response = await this.$saveReferralSource.saveReferralSource();
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.$modal.close();
        this.reload();
        return response;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadSourcesForReferral(): Promise<void> {
        const includes: string[] = [];
        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadReferralSources(options, includes);
            this.referralSources = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateReferralSource(referralSource: CM.ReferralSource, isActive: boolean) {
        if(!isActive) {
            const result = await confirmation.showDeactivate('cm:referral.referredBy.$');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }

        try {
            this.state.beginSave();
            const response = await this.caseService.activateReferralSource(referralSource.id!, isActive);

            if(response.isSuccessful) {
                this.loadSourcesForReferral();
            }
            this.toaster.standard.activate('cm:referral.referredBy.$', isActive, response.isSuccessful);

        } catch(error) {
            this.toaster.standard.activate('cm:referral.referredBy.$', isActive, false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

}
